/**
 * Comprehensive error handling utilities for Financial Dashboard functionality
 */

export enum FinancialErrorType {
  // Data Retrieval Errors
  FINANCIAL_DATA_NOT_FOUND = 'FINANCIAL_DATA_NOT_FOUND',
  PROPERTY_ACCESS_DENIED = 'PROPERTY_ACCESS_DENIED',
  INVALID_DATE_RANGE = 'INVALID_DATE_RANGE',
  DATA_SYNCHRONIZATION_FAILED = 'DATA_SYNCHRONIZATION_FAILED',
  STALE_DATA_WARNING = 'STALE_DATA_WARNING',
  
  // Calculation Errors
  CALCULATION_ERROR = 'CALCULATION_ERROR',
  INVALID_FINANCIAL_DATA = 'INVALID_FINANCIAL_DATA',
  CURRENCY_CONVERSION_FAILED = 'CURRENCY_CONVERSION_FAILED',
  METRICS_CALCULATION_FAILED = 'METRICS_CALCULATION_FAILED',
  
  // Export Errors
  EXPORT_GENERATION_FAILED = 'EXPORT_GENERATION_FAILED',
  EXPORT_FORMAT_UNSUPPORTED = 'EXPORT_FORMAT_UNSUPPORTED',
  EXPORT_SIZE_LIMIT_EXCEEDED = 'EXPORT_SIZE_LIMIT_EXCEEDED',
  EXPORT_TIMEOUT = 'EXPORT_TIMEOUT',
  EXPORT_DOWNLOAD_FAILED = 'EXPORT_DOWNLOAD_FAILED',
  
  // Report Generation Errors
  REPORT_GENERATION_FAILED = 'REPORT_GENERATION_FAILED',
  REPORT_TEMPLATE_ERROR = 'REPORT_TEMPLATE_ERROR',
  REPORT_DATA_INSUFFICIENT = 'REPORT_DATA_INSUFFICIENT',
  REPORT_EMAIL_FAILED = 'REPORT_EMAIL_FAILED',
  REPORT_STORAGE_FAILED = 'REPORT_STORAGE_FAILED',
  
  // User Preferences Errors
  PREFERENCES_SAVE_FAILED = 'PREFERENCES_SAVE_FAILED',
  PREFERENCES_LOAD_FAILED = 'PREFERENCES_LOAD_FAILED',
  INVALID_PREFERENCES_DATA = 'INVALID_PREFERENCES_DATA',
  
  // Chart and Visualization Errors
  CHART_RENDERING_FAILED = 'CHART_RENDERING_FAILED',
  CHART_DATA_INVALID = 'CHART_DATA_INVALID',
  CHART_LIBRARY_ERROR = 'CHART_LIBRARY_ERROR',
  
  // Performance and Resource Errors
  MEMORY_LIMIT_EXCEEDED = 'MEMORY_LIMIT_EXCEEDED',
  PROCESSING_TIMEOUT = 'PROCESSING_TIMEOUT',
  CONCURRENT_REQUEST_LIMIT = 'CONCURRENT_REQUEST_LIMIT',
  
  // Authentication and Authorization
  FINANCIAL_ACCESS_DENIED = 'FINANCIAL_ACCESS_DENIED',
  SUBSCRIPTION_REQUIRED = 'SUBSCRIPTION_REQUIRED',
  FEATURE_NOT_AVAILABLE = 'FEATURE_NOT_AVAILABLE',
  
  // General Errors
  UNKNOWN_FINANCIAL_ERROR = 'UNKNOWN_FINANCIAL_ERROR',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  NETWORK_ERROR = 'NETWORK_ERROR',
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED'
}

export interface FinancialError {
  type: FinancialErrorType;
  message: string;
  details?: string;
  code?: string;
  statusCode?: number;
  retryable?: boolean;
  retryAfter?: number; // seconds
  suggestedAction?: string;
  helpUrl?: string;
  context?: Record<string, any>;
  userMessage?: string; // User-friendly message
  severity?: 'low' | 'medium' | 'high' | 'critical';
}

export interface ErrorDisplayInfo {
  title: string;
  message: string;
  details?: string;
  actionText?: string;
  actionUrl?: string;
  retryable: boolean;
  severity: 'error' | 'warning' | 'info';
  icon?: string;
  showDetails?: boolean;
}

/**
 * Financial error handler for dashboard-related errors
 */
export class FinancialErrorHandler {
  
  /**
   * Converts API errors to FinancialError format
   */
  static parseApiError(error: any, context?: Record<string, any>): FinancialError {
    // Handle axios errors
    if (error.response) {
      const { status, data } = error.response;
      
      // Check if backend sent structured error
      if (data && data.error && data.error.code) {
        return {
          type: this.mapErrorCodeToType(data.error.code),
          message: data.error.message,
          details: data.error.details?.message,
          statusCode: status,
          retryable: data.error.details?.retryable || false,
          retryAfter: data.error.details?.retryAfter,
          suggestedAction: data.error.details?.suggestedAction,
          helpUrl: data.error.details?.helpUrl,
          context: { ...data.error.details, ...context },
          userMessage: this.getUserFriendlyMessage(data.error.code, data.error.message),
          severity: this.getErrorSeverity(status, data.error.code)
        };
      }
      
      // Handle standard HTTP errors
      return this.createErrorFromStatus(status, data?.message || error.message, context);
    }
    
    // Handle network errors
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return {
        type: FinancialErrorType.PROCESSING_TIMEOUT,
        message: 'Request timed out',
        details: 'The financial data request took too long to complete',
        retryable: true,
        retryAfter: 30,
        suggestedAction: 'Please try again or reduce the date range for faster processing',
        userMessage: 'The request is taking longer than expected. Please try again.',
        severity: 'medium',
        context
      };
    }
    
    if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
      return {
        type: FinancialErrorType.NETWORK_ERROR,
        message: 'Network connection failed',
        details: 'Unable to connect to the financial data service',
        retryable: true,
        retryAfter: 30,
        suggestedAction: 'Please check your internet connection and try again',
        userMessage: 'Connection problem. Please check your internet and try again.',
        severity: 'medium',
        context
      };
    }
    
    // Handle chart/visualization errors
    if (error.name === 'ChartError' || error.message?.includes('chart')) {
      return {
        type: FinancialErrorType.CHART_RENDERING_FAILED,
        message: 'Chart rendering failed',
        details: error.message,
        retryable: true,
        retryAfter: 5,
        suggestedAction: 'Try refreshing the dashboard or switching chart types',
        userMessage: 'Unable to display chart. Please try refreshing the page.',
        severity: 'low',
        context
      };
    }
    
    // Default unknown error
    return {
      type: FinancialErrorType.UNKNOWN_FINANCIAL_ERROR,
      message: error.message || 'An unexpected error occurred',
      details: 'Please try again or contact support if the problem persists',
      retryable: true,
      retryAfter: 60,
      suggestedAction: 'Please try again later',
      userMessage: 'Something went wrong. Please try again.',
      severity: 'medium',
      context
    };
  }
  
  /**
   * Maps backend error codes to financial error types
   */
  private static mapErrorCodeToType(code: string): FinancialErrorType {
    const codeMap: Record<string, FinancialErrorType> = {
      'FINANCIAL_DATA_NOT_FOUND': FinancialErrorType.FINANCIAL_DATA_NOT_FOUND,
      'PROPERTY_ACCESS_DENIED': FinancialErrorType.PROPERTY_ACCESS_DENIED,
      'INVALID_DATE_RANGE': FinancialErrorType.INVALID_DATE_RANGE,
      'DATA_SYNC_FAILED': FinancialErrorType.DATA_SYNCHRONIZATION_FAILED,
      'CALCULATION_ERROR': FinancialErrorType.CALCULATION_ERROR,
      'EXPORT_FAILED': FinancialErrorType.EXPORT_GENERATION_FAILED,
      'REPORT_FAILED': FinancialErrorType.REPORT_GENERATION_FAILED,
      'PREFERENCES_ERROR': FinancialErrorType.PREFERENCES_SAVE_FAILED,
      'CHART_ERROR': FinancialErrorType.CHART_RENDERING_FAILED,
      'TIMEOUT': FinancialErrorType.PROCESSING_TIMEOUT,
      'RATE_LIMITED': FinancialErrorType.RATE_LIMIT_EXCEEDED,
      'UNAUTHORIZED': FinancialErrorType.FINANCIAL_ACCESS_DENIED,
      'FORBIDDEN': FinancialErrorType.FINANCIAL_ACCESS_DENIED,
      'NOT_FOUND': FinancialErrorType.FINANCIAL_DATA_NOT_FOUND,
      'SERVICE_UNAVAILABLE': FinancialErrorType.SERVICE_UNAVAILABLE
    };
    
    return codeMap[code] || FinancialErrorType.UNKNOWN_FINANCIAL_ERROR;
  }
  
  /**
   * Creates error from HTTP status code
   */
  private static createErrorFromStatus(status: number, message?: string, context?: Record<string, any>): FinancialError {
    switch (status) {
      case 400:
        return {
          type: FinancialErrorType.INVALID_DATE_RANGE,
          message: message || 'Invalid request parameters',
          retryable: false,
          suggestedAction: 'Please check your date range and property selections',
          userMessage: 'Please check your selections and try again.',
          severity: 'low',
          statusCode: status,
          context
        };
      
      case 401:
        return {
          type: FinancialErrorType.FINANCIAL_ACCESS_DENIED,
          message: 'Authentication required',
          retryable: false,
          suggestedAction: 'Please log in and try again',
          userMessage: 'Please log in to access financial data.',
          severity: 'high',
          statusCode: status,
          context
        };
      
      case 403:
        return {
          type: FinancialErrorType.PROPERTY_ACCESS_DENIED,
          message: 'Access denied to financial data',
          retryable: false,
          suggestedAction: 'You may not have permission to view this property\'s financial data',
          userMessage: 'You don\'t have permission to view this financial data.',
          severity: 'medium',
          statusCode: status,
          context
        };
      
      case 404:
        return {
          type: FinancialErrorType.FINANCIAL_DATA_NOT_FOUND,
          message: 'Financial data not found',
          retryable: false,
          suggestedAction: 'Try selecting a different date range or property',
          userMessage: 'No financial data found for the selected criteria.',
          severity: 'low',
          statusCode: status,
          context
        };
      
      case 408:
        return {
          type: FinancialErrorType.PROCESSING_TIMEOUT,
          message: 'Request timeout',
          retryable: true,
          retryAfter: 30,
          suggestedAction: 'Please try again with a smaller date range',
          userMessage: 'Request timed out. Please try again.',
          severity: 'medium',
          statusCode: status,
          context
        };
      
      case 429:
        return {
          type: FinancialErrorType.RATE_LIMIT_EXCEEDED,
          message: 'Too many requests',
          retryable: true,
          retryAfter: 300,
          suggestedAction: 'Please wait a few minutes before trying again',
          userMessage: 'Too many requests. Please wait a moment and try again.',
          severity: 'medium',
          statusCode: status,
          context
        };
      
      case 500:
        return {
          type: FinancialErrorType.UNKNOWN_FINANCIAL_ERROR,
          message: 'Server error',
          retryable: true,
          retryAfter: 60,
          suggestedAction: 'Please try again later',
          userMessage: 'Server error. Please try again later.',
          severity: 'high',
          statusCode: status,
          context
        };
      
      case 503:
        return {
          type: FinancialErrorType.SERVICE_UNAVAILABLE,
          message: 'Financial service temporarily unavailable',
          retryable: true,
          retryAfter: 300,
          suggestedAction: 'Please try again in a few minutes',
          userMessage: 'Service temporarily unavailable. Please try again later.',
          severity: 'high',
          statusCode: status,
          context
        };
      
      default:
        return {
          type: FinancialErrorType.UNKNOWN_FINANCIAL_ERROR,
          message: message || `HTTP ${status} error`,
          retryable: status >= 500,
          retryAfter: 60,
          suggestedAction: 'Please try again later',
          userMessage: 'An error occurred. Please try again.',
          severity: status >= 500 ? 'high' : 'medium',
          statusCode: status,
          context
        };
    }
  }
  
  /**
   * Gets user-friendly message for error types
   */
  private static getUserFriendlyMessage(code: string, originalMessage: string): string {
    const friendlyMessages: Record<string, string> = {
      'FINANCIAL_DATA_NOT_FOUND': 'No financial data found for your selection.',
      'PROPERTY_ACCESS_DENIED': 'You don\'t have access to this property\'s financial data.',
      'INVALID_DATE_RANGE': 'Please select a valid date range.',
      'DATA_SYNC_FAILED': 'Financial data is temporarily out of sync.',
      'CALCULATION_ERROR': 'Unable to calculate financial metrics.',
      'EXPORT_FAILED': 'Failed to generate export file.',
      'REPORT_FAILED': 'Unable to generate report.',
      'CHART_ERROR': 'Unable to display chart.',
      'TIMEOUT': 'Request took too long to complete.',
      'RATE_LIMITED': 'Too many requests. Please wait a moment.',
      'SERVICE_UNAVAILABLE': 'Financial service is temporarily unavailable.'
    };
    
    return friendlyMessages[code] || originalMessage || 'An error occurred.';
  }
  
  /**
   * Determines error severity based on status and code
   */
  private static getErrorSeverity(status: number, code: string): 'low' | 'medium' | 'high' | 'critical' {
    // Critical errors that prevent core functionality
    if (status === 401 || code === 'FINANCIAL_ACCESS_DENIED') {
      return 'critical';
    }
    
    // High severity for server errors and service unavailable
    if (status >= 500 || code === 'SERVICE_UNAVAILABLE') {
      return 'high';
    }
    
    // Medium severity for client errors and timeouts
    if (status >= 400 || code === 'TIMEOUT' || code === 'RATE_LIMITED') {
      return 'medium';
    }
    
    // Low severity for data not found and chart errors
    return 'low';
  }
  
  /**
   * Formats error for user display
   */
  static formatForDisplay(error: FinancialError): ErrorDisplayInfo {
    const errorMap: Partial<Record<FinancialErrorType, Partial<ErrorDisplayInfo>>> = {
      [FinancialErrorType.FINANCIAL_DATA_NOT_FOUND]: {
        title: 'No Financial Data',
        severity: 'info',
        icon: '📊'
      },
      [FinancialErrorType.PROPERTY_ACCESS_DENIED]: {
        title: 'Access Denied',
        severity: 'error',
        icon: '🔒'
      },
      [FinancialErrorType.INVALID_DATE_RANGE]: {
        title: 'Invalid Date Range',
        severity: 'warning',
        icon: '📅'
      },
      [FinancialErrorType.DATA_SYNCHRONIZATION_FAILED]: {
        title: 'Data Sync Issue',
        severity: 'warning',
        icon: '🔄'
      },
      [FinancialErrorType.CALCULATION_ERROR]: {
        title: 'Calculation Error',
        severity: 'error',
        icon: '🧮'
      },
      [FinancialErrorType.EXPORT_GENERATION_FAILED]: {
        title: 'Export Failed',
        severity: 'error',
        icon: '📤'
      },
      [FinancialErrorType.REPORT_GENERATION_FAILED]: {
        title: 'Report Generation Failed',
        severity: 'error',
        icon: '📋'
      },
      [FinancialErrorType.CHART_RENDERING_FAILED]: {
        title: 'Chart Display Error',
        severity: 'warning',
        icon: '📈'
      },
      [FinancialErrorType.PROCESSING_TIMEOUT]: {
        title: 'Request Timeout',
        severity: 'warning',
        icon: '⏱️'
      },
      [FinancialErrorType.RATE_LIMIT_EXCEEDED]: {
        title: 'Too Many Requests',
        severity: 'warning',
        icon: '🚦'
      },
      [FinancialErrorType.SERVICE_UNAVAILABLE]: {
        title: 'Service Unavailable',
        severity: 'error',
        icon: '🔧'
      },
      [FinancialErrorType.NETWORK_ERROR]: {
        title: 'Connection Error',
        severity: 'warning',
        icon: '🌐'
      }
    };
    
    const errorInfo = errorMap[error.type] || {};
    
    let message = error.userMessage || error.message;
    if (error.details && error.severity !== 'low') {
      message += `\n\n${error.details}`;
    }
    
    let actionText = 'Try Again';
    let actionUrl = error.helpUrl;
    
    if (error.suggestedAction) {
      message += `\n\n${error.suggestedAction}`;
    }
    
    if (error.retryAfter) {
      const minutes = Math.ceil(error.retryAfter / 60);
      message += `\n\nYou can try again in ${minutes} minute${minutes > 1 ? 's' : ''}.`;
      actionText = `Retry in ${minutes}m`;
    }
    
    if (error.helpUrl) {
      actionText = 'Get Help';
    }
    
    return {
      title: errorInfo.title || 'Financial Dashboard Error',
      message,
      details: error.details,
      actionText: error.retryable ? actionText : undefined,
      actionUrl,
      retryable: error.retryable || false,
      severity: errorInfo.severity || 'error',
      icon: errorInfo.icon,
      showDetails: error.severity === 'high' || error.severity === 'critical'
    };
  }
  
  /**
   * Determines if error should trigger automatic retry
   */
  static shouldAutoRetry(error: FinancialError, attemptCount: number = 0): boolean {
    if (!error.retryable || attemptCount >= 3) {
      return false;
    }
    
    // Don't auto-retry user errors
    const userErrorTypes = [
      FinancialErrorType.INVALID_DATE_RANGE,
      FinancialErrorType.PROPERTY_ACCESS_DENIED,
      FinancialErrorType.FINANCIAL_ACCESS_DENIED,
      FinancialErrorType.FINANCIAL_DATA_NOT_FOUND
    ];
    
    if (userErrorTypes.includes(error.type)) {
      return false;
    }
    
    // Don't auto-retry rate limits
    if (error.type === FinancialErrorType.RATE_LIMIT_EXCEEDED) {
      return false;
    }
    
    return true;
  }
  
  /**
   * Calculates retry delay with exponential backoff
   */
  static getRetryDelay(error: FinancialError, attemptCount: number = 0): number {
    if (error.retryAfter) {
      return error.retryAfter * 1000; // Convert to milliseconds
    }
    
    // Exponential backoff: 2s, 4s, 8s, 16s, etc.
    const baseDelay = 2000;
    return Math.min(baseDelay * Math.pow(2, attemptCount), 30000); // Max 30 seconds
  }
  
  /**
   * Gets user-friendly error message for common scenarios
   */
  static getQuickMessage(error: FinancialError): string {
    return error.userMessage || error.message;
  }

  /**
   * Gets suggested solutions for common error types
   */
  static getSuggestedSolutions(error: FinancialError): string[] {
    const solutions: Partial<Record<FinancialErrorType, string[]>> = {
      [FinancialErrorType.FINANCIAL_DATA_NOT_FOUND]: [
        'Try selecting a different date range',
        'Check if you have bookings or expenses in the selected period',
        'Ensure the selected properties have financial data'
      ],
      [FinancialErrorType.INVALID_DATE_RANGE]: [
        'Make sure the start date is before the end date',
        'Select a date range within the last 2 years',
        'Try using one of the preset date ranges'
      ],
      [FinancialErrorType.DATA_SYNCHRONIZATION_FAILED]: [
        'Wait a few minutes for data to sync',
        'Try refreshing the dashboard',
        'Check if recent bookings or expenses are still processing'
      ],
      [FinancialErrorType.EXPORT_GENERATION_FAILED]: [
        'Try reducing the date range for smaller exports',
        'Select fewer properties to reduce data size',
        'Try a different export format (CSV instead of PDF)'
      ],
      [FinancialErrorType.CHART_RENDERING_FAILED]: [
        'Try refreshing the page',
        'Switch to a different chart type',
        'Clear your browser cache and try again'
      ],
      [FinancialErrorType.PROCESSING_TIMEOUT]: [
        'Try selecting a smaller date range',
        'Select fewer properties to reduce processing time',
        'Try again during off-peak hours'
      ],
      [FinancialErrorType.NETWORK_ERROR]: [
        'Check your internet connection',
        'Try again in a few minutes',
        'Disable any VPN or proxy connections'
      ]
    };

    return solutions[error.type] || [
      'Try the operation again',
      'Check your internet connection',
      'Contact support if the problem persists'
    ];
  }
  
  /**
   * Logs error for debugging and monitoring
   */
  static async logError(error: FinancialError, context?: Record<string, any>): Promise<void> {
    const logData = {
      type: error.type,
      message: error.message,
      statusCode: error.statusCode,
      retryable: error.retryable,
      severity: error.severity,
      context: { ...error.context, ...context }
    };
    
    // Log based on severity
    switch (error.severity) {
      case 'critical':
        console.error('Financial Dashboard Critical Error:', logData);
        break;
      case 'high':
        console.error('Financial Dashboard Error:', logData);
        break;
      case 'medium':
        console.warn('Financial Dashboard Warning:', logData);
        break;
      case 'low':
        console.info('Financial Dashboard Info:', logData);
        break;
      default:
        console.log('Financial Dashboard Log:', logData);
    }
    
    // In production, send to monitoring service
    if (process.env.NODE_ENV === 'production' && error.severity && ['critical', 'high'].includes(error.severity)) {
      // Send to monitoring service
      await fetch('/monitoring/errors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ error, logData })
      }).catch(err => console.error('Failed to send error to monitoring service:', err));
    }
  }
}

/**
 * Hook for handling financial dashboard errors with automatic retry logic
 */
export function useFinancialErrorHandler() {
  const handleError = (error: any, context?: Record<string, any>) => {
    const financialError = FinancialErrorHandler.parseApiError(error, context);
    FinancialErrorHandler.logError(financialError, context);
    return financialError;
  };
  
  const formatError = (error: FinancialError) => {
    return FinancialErrorHandler.formatForDisplay(error);
  };
  
  const shouldRetry = (error: FinancialError, attemptCount: number = 0) => {
    return FinancialErrorHandler.shouldAutoRetry(error, attemptCount);
  };
  
  const getRetryDelay = (error: FinancialError, attemptCount: number = 0) => {
    return FinancialErrorHandler.getRetryDelay(error, attemptCount);
  };
  
  const getSolutions = (error: FinancialError) => {
    return FinancialErrorHandler.getSuggestedSolutions(error);
  };
  
  return {
    handleError,
    formatError,
    shouldRetry,
    getRetryDelay,
    getSolutions
  };
}